import React, { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import {
  Anchor,
  Button,
  Checkbox,
  PasswordValidationFeedback,
  Text,
} from '@cubeyou-team/hx-react-components';
import { zodResolver } from '@hookform/resolvers/zod';

import { JOB_ROLE_OPTIONS } from '@discover-frontend/constants';

import { PRIVACY_URL } from '../../../../../../constants/config';
import { Form, PasswordFormItem } from '../../../../../../components';
import InputFormItem from '../../../../../../components/InputFormItem';
import { ROUTES } from '../../../../../../constants/routes';
import translations from '../../../../../../lang';
import { newPasswordRules } from '../../../../../../utils';
import { isAbortError } from '../../../../../../utils/fetchJson';

import { FormSelect } from './components';
import { useGetTrialCountries } from './api/hooks/use-get-trial-countries';
import { useTrialPanelsQuery } from './api/hooks/useTrialPanelsQuery';
import { useTrialSignupMutation } from './api/hooks/useTrialSignupMutation';
import { TrialSignupApiError } from './api/request';
import { useTrialEmailValidation } from './hooks/useTrialEmailValidation';
import { trialSignupSchema, type TrialSignupFormValues } from './schema/schema';

const { TRIAL_SIGNUP } = translations['en-US'];

const baseDefaultValues = {
  companyName: '',
  firstName: '',
  lastName: '',
  jobRole: '',
  countryCode: '',
  email: '',
  targetPanel: '',
  password: '',
  confirmPassword: '',
  acceptedTerms: false,
} as unknown as TrialSignupFormValues;

interface TrialSignupContentProps {
  inviteEmail: string;
  inviteId: string;
  inviteVerificationCode: string;
}

export const TrialSignupContent: React.FC<TrialSignupContentProps> = ({
  inviteEmail,
  inviteId,
  inviteVerificationCode,
}) => {
  const history = useHistory();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { mutateAsync: registerTrialSignup, isLoading: isSubmitting } =
    useTrialSignupMutation();
  const {
    data: panelOptions = [],
    isLoading: isPanelsLoading,
    isError: isPanelsError,
  } = useTrialPanelsQuery();
  const {
    data: countryOptions = [],
    isLoading: isCountriesLoading,
    isError: isCountriesError,
  } = useGetTrialCountries();
  const isPanelsUnavailable =
    isPanelsError || (!isPanelsLoading && panelOptions.length === 0);
  const isCountriesUnavailable =
    isCountriesError || (!isCountriesLoading && countryOptions.length === 0);
  const panelsErrorMessage = isPanelsUnavailable
    ? TRIAL_SIGNUP.ERRORS.PANELS_LOAD_FAILED
    : null;
  const countriesErrorMessage = isCountriesUnavailable
    ? TRIAL_SIGNUP.ERRORS.COUNTRIES_LOAD_FAILED
    : null;

  const defaultValues = useMemo(
    () =>
      ({
        ...baseDefaultValues,
        email: inviteEmail,
      } as TrialSignupFormValues),
    [inviteEmail]
  );

  const {
    control,
    handleSubmit,
    watch,
    setError,
    clearErrors,
    getValues,
    getFieldState,
    trigger,
    formState: { errors, isValid },
  } = useForm<TrialSignupFormValues>({
    resolver: zodResolver(trialSignupSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    isEmailCheckBlockingSubmit,
    isEmailVerified,
    handleEmailChange,
    handleEmailBlur,
    ensureEmailValidForSubmit,
  } = useTrialEmailValidation({
    setError,
    clearErrors,
    getValues,
    getFieldState,
    trigger,
    initialEmail: inviteEmail,
  });

  const password = watch('password') || '';

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);

    if (!(await ensureEmailValidForSubmit(values.email))) {
      return;
    }

    try {
      await registerTrialSignup({
        values,
        invite: {
          id: inviteId,
          verification_code: inviteVerificationCode,
        },
      });
      history.push(ROUTES.LOGIN);
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }

      setSubmitError(
        error instanceof TrialSignupApiError
          ? error.message
          : TRIAL_SIGNUP.ERRORS.REGISTER_FAILED
      );
    }
  });

  const isSubmitDisabled =
    !isValid || isEmailCheckBlockingSubmit || isSubmitting;

  return (
    <div className="min-h-48">
      <div className="flex justify-center pb-6">
        <Text variant="dark" size="xheadline">
          {TRIAL_SIGNUP.TITLE}
        </Text>
      </div>

      <Form className="flex flex-col gap-2" onSubmit={onSubmit}>
        <Controller
          name="companyName"
          control={control}
          render={({ field }) => (
            <InputFormItem
              label={TRIAL_SIGNUP.FIELDS.COMPANY_NAME}
              type="text"
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={Boolean(errors.companyName)}
              message={errors.companyName?.message}
              autoFocus
            />
          )}
        />
        <div className="grid grid-cols-2 gap-4">
          <Controller
            name="firstName"
            control={control}
            render={({ field }) => (
              <div>
                <InputFormItem
                  label={TRIAL_SIGNUP.FIELDS.FIRST_NAME}
                  type="text"
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={Boolean(errors.firstName)}
                  message={errors.firstName?.message}
                />
              </div>
            )}
          />

          <Controller
            name="lastName"
            control={control}
            render={({ field }) => (
              <div>
                <InputFormItem
                  label={TRIAL_SIGNUP.FIELDS.LAST_NAME}
                  type="text"
                  name={field.name}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  error={Boolean(errors.lastName)}
                  message={errors.lastName?.message}
                />
              </div>
            )}
          />
        </div>
        <Controller
          name="jobRole"
          control={control}
          render={({ field }) => (
            <div>
              <FormSelect
                label={TRIAL_SIGNUP.FIELDS.JOB_ROLE}
                integratedLabel
                error={Boolean(errors.jobRole)}
                name={field.name}
                size="small"
                placeholder={TRIAL_SIGNUP.PLACEHOLDERS.JOB_ROLE}
                options={JOB_ROLE_OPTIONS.map((option) => ({
                  ...option,
                  isActive: option.value === field.value,
                }))}
                showAllOption={false}
                onItemChange={(itemValue: unknown) => {
                  const next = Array.isArray(itemValue)
                    ? itemValue[0]
                    : itemValue;
                  field.onChange(
                    next == null || next === 'all' ? '' : String(next)
                  );
                }}
                onBlur={field.onBlur}
              />
              {errors.jobRole?.message && (
                <div className="mt-1" role="alert">
                  <Text tag="span" variant="error" size="small">
                    {errors.jobRole.message}
                  </Text>
                </div>
              )}
            </div>
          )}
        />
        <Controller
          name="countryCode"
          control={control}
          render={({ field }) => (
            <div>
              <FormSelect
                label={TRIAL_SIGNUP.FIELDS.COUNTRY}
                integratedLabel
                error={Boolean(errors.countryCode) || isCountriesUnavailable}
                name={field.name}
                size="small"
                disabled={isCountriesUnavailable || isCountriesLoading}
                placeholder={TRIAL_SIGNUP.PLACEHOLDERS.COUNTRY}
                options={countryOptions.map(({ label, value }) => ({
                  label,
                  value,
                  isActive: value === field.value,
                }))}
                showAllOption={false}
                onItemChange={(itemValue: unknown) => {
                  const next = Array.isArray(itemValue)
                    ? itemValue[0]
                    : itemValue;
                  field.onChange(
                    next == null || next === 'all' ? '' : String(next)
                  );
                }}
                onBlur={field.onBlur}
              />
              {(errors.countryCode?.message || countriesErrorMessage) && (
                <div className="mt-1" role="alert">
                  <Text tag="span" variant="error" size="small">
                    {errors.countryCode?.message || countriesErrorMessage}
                  </Text>
                </div>
              )}
            </div>
          )}
        />
        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <div>
              <InputFormItem
                label={TRIAL_SIGNUP.FIELDS.EMAIL}
                type="email"
                name={field.name}
                value={field.value}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleEmailChange(event, field.onChange)
                }
                onBlur={() => handleEmailBlur(field.onBlur)}
                error={Boolean(errors.email)}
                message={errors.email?.message as string}
                autoComplete="email"
                disabled
              />
              {isEmailVerified && !errors.email && (
                <div className="mt-1" role="status">
                  <Text tag="span" variant="success" size="small">
                    {TRIAL_SIGNUP.VALIDATION.EMAIL_VERIFIED}
                  </Text>
                </div>
              )}
            </div>
          )}
        />
        <Controller
          name="targetPanel"
          control={control}
          render={({ field }) => (
            <div>
              <FormSelect
                label={TRIAL_SIGNUP.FIELDS.TARGET_PANEL}
                integratedLabel
                error={Boolean(errors.targetPanel) || isPanelsUnavailable}
                name={field.name}
                size="small"
                disabled={isPanelsUnavailable || isPanelsLoading}
                placeholder={TRIAL_SIGNUP.PLACEHOLDERS.TARGET_PANEL}
                options={panelOptions.map(({ label, value }) => ({
                  label,
                  value,
                  isActive: value === field.value,
                }))}
                showAllOption={false}
                onItemChange={(itemValue: unknown) => {
                  const next = Array.isArray(itemValue)
                    ? itemValue[0]
                    : itemValue;
                  field.onChange(
                    next == null || next === 'all' ? '' : String(next)
                  );
                }}
                onBlur={field.onBlur}
              />
              {(errors.targetPanel?.message || panelsErrorMessage) && (
                <div className="mt-1" role="alert">
                  <Text tag="span" variant="error" size="small">
                    {errors.targetPanel?.message || panelsErrorMessage}
                  </Text>
                </div>
              )}
            </div>
          )}
        />
        <Controller
          name="password"
          control={control}
          rules={{ deps: ['confirmPassword'] }}
          render={({ field }) => (
            <PasswordFormItem
              formItemVariant="dark_lighter"
              onPasswordChange={field.onChange}
              name={field.name}
              autoComplete="new-password"
              value={field.value}
              onBlur={field.onBlur}
              error={Boolean(errors.password)}
              errorMessage={errors.password?.message}
              label={TRIAL_SIGNUP.FIELDS.PASSWORD}
            />
          )}
        />
        <Text size="medium" className="py-2">
          {TRIAL_SIGNUP.PASSWORD_SECURE}
        </Text>

        <PasswordValidationFeedback
          rules={newPasswordRules}
          password={password}
          onValidationChange={() => {}}
          variant="dark"
          persistRules
        />
        <Controller
          name="confirmPassword"
          control={control}
          render={({ field }) => (
            <PasswordFormItem
              formItemVariant="dark_lighter"
              onPasswordChange={field.onChange}
              name={field.name}
              autoComplete="new-password"
              value={field.value}
              onBlur={field.onBlur}
              error={Boolean(errors.confirmPassword)}
              errorMessage={errors.confirmPassword?.message}
              label={TRIAL_SIGNUP.FIELDS.CONFIRM_PASSWORD}
            />
          )}
        />
        <Controller
          name="acceptedTerms"
          control={control}
          render={({ field }) => (
            <div>
              <Checkbox
                className="hx-checkbox-field flex items-center"
                size="small"
                checked={field.value}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(event.target.checked);
                  trigger('acceptedTerms');
                }}
              >
                <Text tag="span" size="small" variant="dark">
                  {TRIAL_SIGNUP.AGREEMENT.BEFORE_TERMS}
                  <Anchor href={ROUTES.TERMS_AND_CONDITIONS} target="_blank">
                    {TRIAL_SIGNUP.AGREEMENT.TERMS_OF_SERVICE}
                  </Anchor>
                  {TRIAL_SIGNUP.AGREEMENT.MIDDLE}
                  <Anchor href={PRIVACY_URL} target="_blank">
                    {TRIAL_SIGNUP.AGREEMENT.PRIVACY_POLICY}
                  </Anchor>
                  {TRIAL_SIGNUP.AGREEMENT.AFTER_PRIVACY}
                </Text>
              </Checkbox>
              {errors.acceptedTerms?.message && (
                <div className="mt-1" role="alert">
                  <Text tag="span" variant="error" size="small">
                    {errors.acceptedTerms.message}
                  </Text>
                </div>
              )}
            </div>
          )}
        />
        <div className="w-full">
          {submitError && (
            <div className="mb-2" role="alert">
              <Text tag="span" variant="error" size="small">
                {submitError}
              </Text>
            </div>
          )}
          <Button
            className="w-full"
            variant="primary"
            disabled={isSubmitDisabled}
            style={{ borderRadius: '0.375rem' }}
            loading={isSubmitting ? 'true' : undefined}
            type="submit"
          >
            {TRIAL_SIGNUP.SIGN_UP}
          </Button>
        </div>
      </Form>
    </div>
  );
};
